import { sum } from 'lib/array/arrayUtils'
import { floatify } from 'lib/maths/mathUtils'

interface Input {
  /** The cart amount we want to reduce by the classic-reward points portion. */
  amount: number
  /** Currently selected classic reward(s). Accepts a single reward, an array, or null/undefined. */
  classicReward?: App.Checkout.ClassicRewardCheckoutState | null | undefined
  classicRewards?: Array<App.Checkout.ClassicRewardCheckoutState> | null | undefined
  /** Flight items in the cart — used to look up the reward's source flight. */
  flightItems: ReadonlyArray<App.Checkout.FlightItem>
}

interface Output {
  /** `amount` minus the points portion, clamped at zero. */
  amount: number
  /** The cash equivalent of the points portion (>= 0). */
  portion: number
}

/**
 * Computes the points portion for a single classic-reward entry.
 */
function computePortionForReward(
  classicReward: App.Checkout.ClassicRewardCheckoutState,
  flightItems: ReadonlyArray<App.Checkout.FlightItem>,
): number {
  const sourceFlight = flightItems.find(item => item.transactionKey === classicReward.itemTransactionKey)
  if (!sourceFlight) return 0

  const itemTotal = sourceFlight.totalFare
    ?? sum(sourceFlight.flights.map(f => f.fareFamily?.cost?.all ?? f.cost ?? 0))

  return Math.max(0, itemTotal - classicReward.totalCash)
}

/**
 * Applies the classic-reward points deduction to a cart amount.
 *
 * Classic rewards split a flight booking into a points portion (covered by
 * burned LE points) and a cash co-pay. The Stripe charge and the displayed
 * cart total must both be reduced by the points portion; otherwise the
 * customer is charged for the points-covered amount on top of having their
 * points burned (a double-payment).
 *
 * Accepts either `classicReward` (single, backward-compat) or `classicRewards`
 * (array). When both are provided, the array takes precedence. The returned
 * portion is the sum of all individual reward portions.
 */
export function applyClassicRewardDeduction({ amount, classicReward, classicRewards, flightItems }: Input): Output {
  // Normalise to an array, preferring the array form when provided.
  const rewards: ReadonlyArray<App.Checkout.ClassicRewardCheckoutState> =
    classicRewards?.length ? classicRewards
      : classicReward ? [classicReward]
        : []

  if (rewards.length === 0) {
    return { amount, portion: 0 }
  }

  let totalPortion = 0
  for (const reward of rewards) {
    totalPortion += computePortionForReward(reward, flightItems)
  }

  const reducedAmount = floatify(Math.max(0, amount - totalPortion))
  return { amount: reducedAmount, portion: totalPortion }
}
